/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import type { EuiSearchBarProps } from '@elastic/eui'
import { EuiInMemoryTable, EuiLink, EuiTextColor } from '@elastic/eui'

import type { TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import { FormattedMessage } from '@modules/i18n'
import { useListDeploymentsQuery } from '@modules/cloud-lib/deployments/hooks'

import { deploymentUrl } from '@/lib/urlBuilder' // eslint-disable-line import/no-restricted-paths

type ResourceAssociation = {
  resourceId: string
  resourceType: string
  resourceName: string
  rulesetName: string
}

type Props = {
  rulesets: TrafficFilterRulesetInfo[]
}

const PAGINATE_FLOOR = 10 // the number of displayable rows before the table will paginate

const ProtectedResourcesList: React.FunctionComponent<Props> = ({ rulesets }) => {
  const navigate = useNavigate()

  const { data: deploymentsData, isLoading: isLoadingDeployments } = useListDeploymentsQuery()

  // Map of deployment IDs to names since we don't get the names through the rulesets
  const deploymentMap = useMemo(() => {
    const map = new Map<string, string>()

    if (deploymentsData?.deployments) {
      deploymentsData.deployments.forEach((deployment) => {
        map.set(deployment.id, deployment.name)
      })
    }

    return map
  }, [deploymentsData])

  const associations = useMemo(() => {
    const result: ResourceAssociation[] = []

    rulesets.forEach((ruleset) => {
      if (ruleset.associations && ruleset.associations.length > 0) {
        ruleset.associations.forEach((association) => {
          const resourceName =
            association.entity_type === 'deployment'
              ? deploymentMap.get(association.id) || association.id
              : association.id

          result.push({
            resourceId: association.id,
            resourceType: association.entity_type,
            resourceName,
            rulesetName: ruleset.name,
          })
        })
      }
    })

    return result
  }, [rulesets, deploymentMap])

  const resourceTypes = useMemo(
    () =>
      new Set(
        rulesets.flatMap((ruleset) =>
          ruleset.associations?.map((association) => association.entity_type),
        ),
      ),
    [rulesets],
  )

  const resourceColumnTitle = useMemo(() => {
    if (resourceTypes.size === 1 && resourceTypes.has('deployment')) {
      return (
        <FormattedMessage
          id='networkSecurity.protectedDeployments.resourceColumnTitle'
          defaultMessage='Deployment name'
        />
      )
    }

    if (resourceTypes.size === 1 && resourceTypes.has('project')) {
      return (
        <FormattedMessage
          id='networkSecurity.protectedProjects.resourceColumnTitle'
          defaultMessage='Project name'
        />
      )
    }

    return (
      <FormattedMessage
        id='networkSecurity.protectedResources.resourceColumnTitle'
        defaultMessage='Resource name'
      />
    )
  }, [resourceTypes])

  const columns = [
    {
      field: 'resourceName',
      name: resourceColumnTitle,
      sortable: true,
      render: (_, { resourceName, resourceId, resourceType }: ResourceAssociation) => {
        const url = resourceType === 'deployment' && deploymentUrl(resourceId)
        const text = resourceName || <EuiTextColor color='subdued'>{resourceId}</EuiTextColor>

        if (!resourceName || !url) {
          return text
        }

        return (
          <EuiLink
            href={url}
            onClick={(e) => {
              e.preventDefault()
              navigate(url)
            }}
          >
            {text}
          </EuiLink>
        )
      },
    },
    {
      field: 'rulesetName',
      name: (
        <FormattedMessage
          id='networkSecurity.protectedResources.ruleset.header'
          defaultMessage='Network security'
        />
      ),
    },
  ]

  const sorting = {
    sort: {
      field: 'resourceName',
      direction: 'asc' as const,
    },
  }

  const search: EuiSearchBarProps = {
    box: {
      placeholder: 'Search',
      incremental: true,
    },
  }

  return (
    <EuiInMemoryTable
      loading={isLoadingDeployments}
      items={associations}
      columns={columns}
      sorting={sorting}
      pagination={associations.length > PAGINATE_FLOOR}
      tableLayout='auto'
      search={search}
    />
  )
}

export default ProtectedResourcesList
